import config from '@/config'
import Channels from '@/models/Channels'
import { ChannelRoles } from '@/models/Users'
import { useGlobalStore } from '@/store/app'
import { PaginationParams } from '@/types/PaginationParams'
import BaseService from './BaseService'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class ChannelsServiceCls extends BaseService<Channels> {
  constructor() {
    super(null as any)
  }

  model() {
    return Channels
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}/channels`
  }

  async create(data: any): Promise<any> {
    const response = await http.post(`${this.endpoint}`, data)
    return response.data
  }

  async update(data: any): Promise<any> {
    const response = await http.patch(`${this.endpoint}/${data._id}`, data)
    return response.data
  }

  async delete(data: any): Promise<any> {
    const response = await http.delete(`${this.endpoint}/${data._id}`, data)
    return response.data
  }

  async fetchAllChannels(noCache?: boolean): Promise<any> {
    const headers = noCache ? { 'Cache-Control': 'no-cache' } : {}
    const response = await http.get(`${this.endpoint}`, { headers })
    return response.data
  }

  async fetchChannelsMetadata(): Promise<any> {
    const response = await http.get(`${this.endpoint}/metadata`)
    return response.data
  }

  async getChannelBySlug(slug: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/slug/${slug}`)
    return response.data
  }

  async fetchChannelMembers(
    channelId: string,
    roles?: string,
    paginationParams?: PaginationParams,
    searchText?: string
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/${channelId}/users`, {
      params: {
        roles,
        searchText,
        ...paginationParams,
      },
    })
    return response.data
  }

  async addChannelMembers(membersData: any, channelId: string): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/${channelId}/users`,
      membersData
    )
    return response.data
  }

  async updateMemberRole(
    channelId: string,
    contactId: string,
    role: ChannelRoles
  ): Promise<any> {
    const response = await http.patch(
      `${this.endpoint}/${channelId}/users/${contactId}?updatedRole=${role}`
    )
    return response.data
  }

  async removeUserFromChannel(
    channelId: string,
    contactId: string
  ): Promise<any> {
    const response = await http.delete(
      `${this.endpoint}/${channelId}/users/${contactId}`
    )
    return response.data
  }

  async leaveChannel(channelId: string): Promise<any> {
    const response = await http.delete(
      `${this.endpoint}/${channelId}/users/leave-channel`
    )
    return response.data
  }

  async updateChannelVisibility(
    channelId: string,
    visibility: string
  ): Promise<any> {
    const response = await http.patch(
      `${this.endpoint}/${channelId}/update-visibility?visibility=${visibility}`
    )
    return response.data
  }

  async markChannelAsRead(channelId: string): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/${channelId}/mark-as-read`
    )
    return response.data
  }
}

// Export singleton instance
export const ChannelsService = new ChannelsServiceCls()

// Export type for dependency injection
export type IChannelsService = ChannelsServiceCls

// Export commonly used methods directly
export const {
  create,
  update,
  delete: deleteChannel,
  getChannelBySlug,
  fetchAllChannels,
  fetchChannelMembers,
  addChannelMembers,
  updateMemberRole,
  removeUserFromChannel,
  leaveChannel,
  updateChannelVisibility,
} = ChannelsService
